import React from 'react';
import { Link as RouterLink, generatePath, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import IconButton from '@mui/material/IconButton';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import Tooltip from '@/components/mui/Tooltip';
import * as ACL from '@/domain/restriction';
import ROUTES_PATH from '@/router/routesPathConfig';

const tooltipDescriptor = {
  id: 'company.bankFeed.transactions.header.settings.tooltip',
  defaultMessage: 'Bank Feed Settings',
};

// Permission-gated Settings shortcut, rendered right-aligned on the Bank Feed
// transaction-type tabs row (`ml: 'auto'` pushes it to the trailing edge of the
// flex row that also holds the tabs).
// Gated on BANK_FEED_CAN_VIEW_CONNECTION:
//   - has-permission  → the gear IconButton is rendered
//   - lacks-permission → nothing is rendered (the tabs on the row are unaffected)
// Navigation is a single interaction: the button is a RouterLink to the
// connections settings route (no menu / sidebar traversal / confirmation).
// Only the explicit `companyId` is passed to generatePath (mirroring the
// empty-state CTA) — this sidesteps the Bank Feed splat-route hazard entirely,
// since the `*` param is never carried into the target template.
const SettingsButton: React.FC = () => {
  const intl = useIntl();
  const isGranted = useSelector(ACL.isGranted);
  const { companyId = '' } = useParams<{ companyId: string }>();

  if (!isGranted(ACL.BANK_FEED_CAN_VIEW_CONNECTION)) return null;

  const settingsPath = generatePath(ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_CONNECTIONS.absolute, {
    companyId,
  });

  return (
    <Tooltip t={tooltipDescriptor}>
      <IconButton
        color="primary"
        component={RouterLink}
        to={settingsPath}
        sx={{ ml: 'auto' }}
        aria-label={intl.formatMessage(tooltipDescriptor)}
      >
        <SettingsOutlinedIcon />
      </IconButton>
    </Tooltip>
  );
};

export default SettingsButton;
